import { useEffect, useRef } from 'react';

/**
 * Shared primitives.
 *
 * Every asynchronous surface in the app needs the same four states (§12.20), so
 * they exist once here rather than being re-invented per screen with slightly
 * different copy and slightly worse accessibility.
 */

export function Button({ variant = 'secondary', loading = false, children, ...props }) {
  return (
    <button
      type="button"
      className={`button button--${variant}${loading ? ' button--loading' : ''}`}
      // A loading button that stays clickable is a duplicate-submission bug.
      disabled={loading || props.disabled}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <span className="spinner" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

export function Card({ title, action, children, className = '' }) {
  return (
    <section className={`card ${className}`}>
      {(title || action) && (
        <header className="card__header">
          {title ? <h2 className="card__title">{title}</h2> : <span />}
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

export function Loading({ label = 'Loading' }) {
  return (
    <div className="state state--loading" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <span>{label}…</span>
    </div>
  );
}

export function Skeleton({ rows = 3 }) {
  return (
    <div className="skeleton" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton__row" />
      ))}
    </div>
  );
}

/**
 * Empty states instruct rather than decorate. §11.2 forbids filling them with
 * sample agents or sample proofs, which would be indistinguishable from real
 * data to someone learning the product.
 */
export function Empty({ title, description, action }) {
  return (
    <div className="state state--empty">
      <p className="state__title">{title}</p>
      {description && <p className="state__description">{description}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ error, onRetry, context }) {
  const code = error?.code ?? 'UNKNOWN';
  const requestId = error?.requestId;
  return (
    <div className="state state--error" role="alert">
      <p className="state__title">{context ?? 'Something went wrong'}</p>
      <p className="state__description">{error?.message ?? 'The request could not be completed.'}</p>
      <p className="state__meta mono">
        {code}
        {requestId ? ` · ${requestId}` : ''}
      </p>
      {onRetry && (
        <Button variant="ghost" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function Badge({ tone = 'neutral', children }) {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

/**
 * Shown on a data screen when there is no signed-in session. The screen and its
 * navigation are still fully visible; this explains that the account's data and
 * the actions on the screen need a connected wallet, and offers the control to
 * do it. It never fabricates sample data to fill the space.
 */
export function ConnectGate({ title, subtitle, onConnect, connectLabel = 'Connect wallet', canConnect = true }) {
  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <h1 className="page-title">{title}</h1>
          {subtitle && <p className="page-subtitle">{subtitle}</p>}
        </div>
      </header>
      <div className="state state--empty">
        <p className="state__title">Connect your wallet to continue</p>
        <p className="state__description">
          Your wallet is your identity in Cortis. Connect and sign in to load this account and to create agents, run
          tasks and authorise workflows. Browsing the interface needs nothing; acting on it does.
        </p>
        {canConnect && onConnect && (
          <Button variant="primary" onClick={onConnect}>{connectLabel}</Button>
        )}
        {!canConnect && (
          <p className="state__meta">No EVM wallet detected. Install one, then reload this page.</p>
        )}
      </div>
    </div>
  );
}

export function Hash({ value, label }) {
  if (!value) return <span className="text-muted">—</span>;
  const short = value.length > 20 ? `${value.slice(0, 10)}…${value.slice(-8)}` : value;
  return (
    <span className="hash">
      <span className="mono" title={value}>
        {short}
      </span>
      <button
        type="button"
        className="hash__copy"
        aria-label={`Copy ${label ?? 'value'}`}
        onClick={() => navigator.clipboard?.writeText(value)}
      >
        copy
      </button>
    </span>
  );
}

/** Modal with a focus trap and escape handling, per §12.9. */
export function Modal({ open, title, onClose, children, footer }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    const node = ref.current;
    node?.querySelector('button, input, select, textarea, [tabindex]')?.focus();

    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key !== 'Tab' || !node) return;
      const focusable = node.querySelectorAll('button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={ref}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal__header">
          <h2 className="modal__title">{title}</h2>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <div className="modal__body">{children}</div>
        {footer && <footer className="modal__footer">{footer}</footer>}
      </div>
    </div>
  );
}

export function Field({ label, hint, error, children, htmlFor }) {
  return (
    <div className="field">
      {/* Visible labels, never placeholder-only. §12.7. */}
      <label className="field__label" htmlFor={htmlFor}>
        {label}
      </label>
      {hint && <p className="field__hint">{hint}</p>}
      {children}
      {error && (
        <p className="field__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function Toast({ toasts, onDismiss }) {
  return (
    <div className="toasts" role="region" aria-label="Notifications">
      {toasts.map((t) => (
        <div key={t.id} className={`toast toast--${t.tone ?? 'neutral'}`} role="status">
          <span>{t.message}</span>
          <button type="button" onClick={() => onDismiss(t.id)} aria-label="Dismiss">
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
